import React from "react";

import { fetchDispatchGeotabReal, fetchGeotabPreferences } from "../api/geotabApi.js";
import { getLocalTodayDispatchDate } from "../utils/dispatchDayCalendarUtils.js";
import { onPreferencesTabReturn } from "../utils/preferencesDelegation.js";
import useLiveUserUiPreference from "./useLiveUserUiPreference.js";

// Réel Geotab du Gantt du Dispatch : lu seulement sur demande, après l'affichage du Dispatch, jamais avant
// (le changement de date ne l'attend pas). Plan de la journée tel qu'affiché, comme « Plan de clôture » de Geotab.
// Chaque version du Dispatch (réaffectation, Chargé PM…) a sa lecture : les numéros d'étapes restent ceux du Gantt.
const TODAY_REFRESH_MS = 2 * 60 * 1000;
const PAST_CACHE_MS = 30 * 60 * 1000;
const cache = new Map();
let thresholds = null;

// Mêmes seuils que les écrans Geotab de l'utilisateur (rayon, tolérance, arrêt hors plan).
function loadThresholds() {
  thresholds ||= fetchGeotabPreferences().catch(() => null).then((preferences) => ({
    radius_m: preferences?.radius_m ?? 1000,
    tolerance_min: preferences?.tolerance_min ?? 15,
    min_off_min: preferences?.min_off_min ?? 10,
  }));
  return thresholds;
}
// Un Admin peut les avoir réglés pour l'utilisateur : la prochaine lecture du réel les relit.
onPreferencesTabReturn(() => { thresholds = null; });

// Ce que les pastilles et les écarts lisent : une lecture identique ne redessine pas le Gantt.
const signatureOf = (trucks) => JSON.stringify(trucks.map((truck) => [truck.truck_id, truck.plan?.departure, truck.plan?.return_at,
  truck.comparison && [truck.comparison.in_progress, truck.comparison.real_departure, truck.comparison.real_return, truck.comparison.sites, truck.comparison.visits]]));

function loadDay(date, period, revision, maxAgeMs, liveLimits = null) {
  const day = `${date}:${period}:`;
  const key = `${day}${revision}:${JSON.stringify(liveLimits)}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < maxAgeMs) return hit.promise;
  const promise = (liveLimits ? Promise.resolve(liveLimits) : loadThresholds()).then(async (limits) => {
    const trucks = (await fetchDispatchGeotabReal(date, period, limits, revision))?.trucks || [];
    return { byTruckId: new Map(trucks.map((truck) => [String(truck.truck_id), truck])), tolerance: limits.tolerance_min, signature: signatureOf(trucks) };
  });
  // Une seule version gardée par journée.
  [...cache.keys()].filter((other) => other.startsWith(day)).forEach((other) => cache.delete(other));
  cache.set(key, { at: Date.now(), promise });
  promise.catch(() => cache.delete(key));
  return promise;
}

// Seuils Geotab de l'utilisateur ; null tant qu'ils ne sont pas lus. Le Réel et le contrôle avant clôture envoient les mêmes :
// le serveur leur sert une seule comparaison.
export function useDispatchGeotabLimits() {
  const preferences = useLiveUserUiPreference("geotab", undefined);
  return React.useMemo(() => preferences === undefined ? null : ({
    radius_m: preferences?.radius_m ?? 1000, tolerance_min: preferences?.tolerance_min ?? 15, min_off_min: preferences?.min_off_min ?? 10,
  }), [preferences === undefined, preferences?.radius_m, preferences?.tolerance_min, preferences?.min_off_min]);
}

export default function useDispatchGeotabReal({ date = "", period = "day", revision = "", enabled = false }) {
  const today = getLocalTodayDispatchDate();
  const limits = useDispatchGeotabLimits();
  const key = enabled && date && date <= today ? `${date}:${period}:${revision}:${JSON.stringify(limits)}` : "";
  const [state, setState] = React.useState({ key: "", data: null, error: "" });
  React.useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const isToday = date === today;
    const load = (maxAgeMs) => loadDay(date, period, revision, maxAgeMs, limits).then(
      (data) => {
        if (cancelled) return;
        setState((current) => (current.key === key && !current.error && current.data?.signature === data.signature ? current : { key, data, error: "" }));
      },
      (error) => { if (!cancelled) setState((current) => ({ key, data: current.key === key ? current.data : null, error: error?.message || "Geotab est indisponible." })); },
    );
    // Le Dispatch d'abord : la lecture part quand le navigateur est libre.
    const idle = window.requestIdleCallback?.(() => load(isToday ? TODAY_REFRESH_MS : PAST_CACHE_MS), { timeout: 1500 });
    const timer = idle == null ? window.setTimeout(() => load(isToday ? TODAY_REFRESH_MS : PAST_CACHE_MS), 300) : null;
    const refresh = isToday ? window.setInterval(() => { if (document.visibilityState === "visible") load(0); }, TODAY_REFRESH_MS) : null;
    return () => {
      cancelled = true;
      if (idle != null) window.cancelIdleCallback?.(idle);
      window.clearTimeout(timer);
      window.clearInterval(refresh);
    };
  }, [key, date, period, revision, today, limits]);
  return key && state.key === key ? state : { key, data: null, error: "" };
}
